import { inject, Injectable } from '@angular/core';
import { BaseService } from '@shared/core';
import { catchError, finalize, map, Observable, shareReplay } from 'rxjs';
import { IAuthCurrentUser, IAuthModel, IAuthToken, IAuthUserModel } from '../../models';
import { DataStorageService } from '../storage';
import { CurrentUserService } from './current-user.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private redirectUrl: string | null = null;

  setRedirectUrl(url: string | null): void {
    this.redirectUrl = url;
  }

  getRedirectUrl(): string | null {
    return this.redirectUrl;
  }

  private refreshInProgress$?: Observable<string>;

  get canRefresh(): boolean {
    return this.refreshInProgress$ == null;
  }

  get authToken(): string | undefined {
    return this.storageService.getAuthToken();
  }

  get isAuthenticated(): boolean {
    return !!this.authToken;
  }

  get authenticated$(): Observable<string | undefined> {
    return this.storageService.onAuthTokenChanged();
  }

  private httpService = inject(BaseService);
  private storageService = inject(DataStorageService);
  private currentUserService = inject(CurrentUserService);

  onLogin(authData: IAuthModel): Observable<unknown> {
    return this.httpService.post('v1/auth/token', authData).pipe(
      map((response: IAuthToken) => {
        const accessToken = response?.accessToken;
        const refreshToken = response?.refreshToken;
        this.storageService.setTokens(accessToken, refreshToken);
        return accessToken != null && refreshToken != null;
      }),
    );
  }

  getCurrentUser(): Observable<IAuthCurrentUser> {
    return this.httpService.get('v1/users/current');
  }

  registerUser(authData: IAuthUserModel): Observable<unknown> {
    return this.httpService.post('v1/auth/register', authData);
  }

  getOtp(authData: IAuthModel): Observable<unknown> {
    return this.httpService.post('v1/auth/get-otp', authData);
  }

  onRefresh(): Observable<string> {
    if (this.refreshInProgress$) {
      return this.refreshInProgress$;
    }
    const refreshToken = this.storageService.getRefreshToken();

    this.refreshInProgress$ = this.httpService
      .post<IAuthToken>('v1/auth/refresh-token', { refreshToken: refreshToken })
      .pipe(
        map(response => {
          this.storageService.setTokens(response?.accessToken, response?.refreshToken);
          return response?.accessToken;
        }),
        catchError(err => {
          this.onLogout();
          throw err;
        }),
        shareReplay(1),
        finalize(() => {
          this.refreshInProgress$ = undefined;
        }),
      );

    return this.refreshInProgress$;
  }

  onLogout(): void {
    this.currentUserService.user.set(null);
    this.storageService.logout();
  }
}
